import { useCallback, useEffect, useRef, useState } from "react";
import {
  ICourseContentDto,
  ICourseDto,
} from "../../../models/dto/trainingApi/ICourseDto";
import courseService from "../../../services/trainingApi/course.service";
import ImageService from "../../../services/generalApi/image.service";
import { encryptText } from "../../../utils/encryptionUtils";
import { StatusCourseEnum } from "../../../models/enum/Courses";

export const useCourses = () => {
  /* =======================
   STATE – UI
======================= */

  // Tabs
  const [selectedCourseTab, setSelectedCourseTab] = useState<number>(0);

  /* =======================
   STATE – DATA
======================= */

  const [coursesData, setCoursesData] = useState<ICourseDto[]>([]);
  const [actualCourse, setActualCourse] = useState<ICourseContentDto>();

  /* =======================
   STATE – LISTS BY STATUS
======================= */

  const [available, setAvailable] = useState<ICourseDto[]>([]);
  const [availableFiltered, setAvailableFiltered] = useState<ICourseDto[]>([]);
  const [inProgress, setInProgress] = useState<ICourseDto[]>([]);
  const [inProgressFiltered, setInProgressFiltered] = useState<ICourseDto[]>(
    []
  );
  const [completed, setCompleted] = useState<ICourseDto[]>([]);
  const [completedFiltered, setCompletedFiltered] = useState<ICourseDto[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  /* =======================
   STATE – SEARCH
======================= */

  const [searchAvailable, setSearchAvailable] = useState<string>("");
  const [searchInProgress, setSearchInProgress] = useState<string>("");
  const [searchCompleted, setSearchCompleted] = useState<string>("");

  /* =======================
   STATE – IMAGES
======================= */

  const imageCache = useRef<Record<string, string>>({});
  const [blobImages, setBlobImages] = useState<{ id: number; url: string }[]>(
    []
  );
  const [imagesLoaded, setImagesLoaded] = useState(false);

  /* =======================
   CALLBACKS
======================= */

  const loadImages = useCallback(async () => {
    const promises = coursesData.map(async (course) => {
      if (imageCache.current[course.coverImage]) {
        return {
          id: course.courseID,
          url: imageCache.current[course.coverImage],
        };
      }

      try {
        const response = await ImageService.getBlobImage(course.coverImage);
        if (response instanceof Blob) {
          const objectUrl = URL.createObjectURL(response);
          imageCache.current[course.coverImage] = objectUrl;
          return { id: course.courseID, url: objectUrl };
        }
      } catch (error) {
        console.error(
          `Error loading image for course ID ${course.courseID}:`,
          error
        );
      }

      return null;
    });

    const results = await Promise.all(promises);
    const filteredResults = results.filter(
      (result): result is { id: number; url: string } => result !== null
    );

    setBlobImages(filteredResults);
    setImagesLoaded(true);
  }, [coursesData]);

  const getCoursesData = useCallback(async () => {
    setLoadingCourses(true);

    try {
      const r = await courseService.getCoursesData();
      fillData(r);
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  /* =======================
   EFFECTS
======================= */

  // Load courses on mount
  useEffect(() => {
    getCoursesData();
  }, [getCoursesData]);

  // Load images when courses change
  useEffect(() => {
    if (coursesData.length > 0) {
      loadImages();
    }
  }, [coursesData, loadImages]);

  /* =======================
   DATA – LOAD & TRANSFORM
======================= */

  const resetCoursesData = () => {
    setCoursesData([]);
    setAvailable([]);
    setAvailableFiltered([]);
    setInProgress([]);
    setInProgressFiltered([]);
    setCompleted([]);
    setCompletedFiltered([]);
    getCoursesData();
  };

  const fillData = (data: ICourseDto[]) => {
    let courseDataTemp: ICourseDto[] = [];
    let availableTemp: ICourseDto[] = [];
    let inProgressTemp: ICourseDto[] = [];
    let completedTemp: ICourseDto[] = [];

    for (let i = 0; i < data.length; i++) {
      let course: ICourseDto = {
        courseID: data[i].courseID,
        courseName: data[i].courseName,
        description: data[i].description,
        startDate: data[i].startDate,
        endDate: data[i].endDate,
        coverImage: data[i].coverImage,
        statusByUser: data[i].statusByUser,
        isReinforcement: Boolean(data[i].isReinforcement),
      };
      courseDataTemp.push(course);
      if (
        [
          StatusCourseEnum.Pending,
          StatusCourseEnum.ReinforcementPending,
        ].includes(data[i].statusByUser)
      ) {
        availableTemp.push(course);
      } else if (
        [
          StatusCourseEnum.InProgress,
          StatusCourseEnum.Failed,
          StatusCourseEnum.CourseFailed,
          StatusCourseEnum.ReinforcementInProgress,
          StatusCourseEnum.ReinforcementFailed,
        ].includes(data[i].statusByUser)
      ) {
        inProgressTemp.push(course);
      } else if (data[i].statusByUser === StatusCourseEnum.Completed) {
        completedTemp.push(course);
      }
    }
    setCoursesData(courseDataTemp);
    setAvailable(availableTemp);
    setAvailableFiltered(availableTemp);
    setInProgress(inProgressTemp);
    setInProgressFiltered(inProgressTemp);
    setCompleted(completedTemp);
    setCompletedFiltered(completedTemp);
  };

  /* =======================
   COURSE CONTENT
======================= */

  const openCourseContent = async (
    courseId: number,
    isReinforcement: boolean
  ) => {
    let r: ICourseContentDto;

    isReinforcement
      ? (r = await courseService.getReinforcementCourseContent(courseId))
      : (r = await courseService.getCourseContent(courseId));

    const courseTemp: ICourseContentDto = {
      courseID: r.courseID,
      courseName: r.courseName,
      url: encryptText(r.url),
      contentType: r.contentType,
      isReinforcement: isReinforcement,
    };

    setActualCourse(courseTemp);
  };

  /* =======================
   IMAGES
======================= */

  const getUrlFromBlob = (courseId: number) => {
    const course = blobImages.find((x) => x.id === courseId);
    return course ? course.url : "";
  };

  /* =======================
   SEARCH HANDLERS
======================= */

  const handleChangeSearchAvailable = (value: string) => {
    setSearchAvailable(value);
    const filterArray = available.filter((element) =>
      element.courseName
        .toString()
        .toLowerCase()
        .includes(String(value).toLowerCase())
    );
    setAvailableFiltered(filterArray);
  };

  const handleChangeSearchInProgress = (value: string) => {
    setSearchInProgress(value);
    const filterArray = inProgress.filter((element) =>
      element.courseName
        .toString()
        .toLowerCase()
        .includes(String(value).toLowerCase())
    );
    setInProgressFiltered(filterArray);
  };

  const handleChangeSearchCompleted = (value: string) => {
    setSearchCompleted(value);
    const filterArray = completed.filter((element) =>
      element.courseName
        .toString()
        .toLowerCase()
        .includes(String(value).toLowerCase())
    );
    setCompletedFiltered(filterArray);
  };

  /* =======================
     RETURN
  ======================== */

  return {
    // tabs
    selectedCourseTab,
    setSelectedCourseTab,

    // course content
    loadingCourses,
    actualCourse,
    openCourseContent,

    // lists
    availableFiltered,
    inProgressFiltered,
    completedFiltered,

    // search
    searchAvailable,
    searchInProgress,
    searchCompleted,
    handleChangeSearchAvailable,
    handleChangeSearchInProgress,
    handleChangeSearchCompleted,

    // images
    imagesLoaded,
    getUrlFromBlob,

    // reset
    resetCoursesData,
  };
};
